/**
 * API client for Wikidata search endpoints (with Lambda fallback)
 */

import apiClient from './client'
import type {
  WikidataComposer,
  WikidataWork,
  SearchComposersResponse,
  GetComposerWorksResponse,
} from '@brent/shared/types'

// Re-export types for convenience
export type { WikidataComposer, WikidataWork, SearchComposersResponse, GetComposerWorksResponse }

/**
 * Search for composers using Lambda + Wikidata fallback
 *
 * This is called when static files don't have matching composers
 *
 * @param searchTerm - Composer name to search for
 * @param limit - Maximum results (default: 10)
 * @returns List of matching composers with Wikidata IDs
 */
export async function searchComposers(
  searchTerm: string,
  limit: number = 10,
): Promise<SearchComposersResponse> {
  return apiClient.get<SearchComposersResponse>('/wikidata/composers', {
    params: {
      q: searchTerm,
      limit,
    },
  })
}

/**
 * Get works by a specific composer using Lambda + Wikidata fallback
 *
 * This is called when static files don't have composer's works
 *
 * @param composerId - Wikidata QID (e.g., "Q254" for Mozart)
 * @param limit - Maximum results (default: 100)
 * @returns List of musical works by the composer
 */
export async function getComposerWorks(
  composerId: string,
  limit: number = 100,
): Promise<GetComposerWorksResponse> {
  return apiClient.get<GetComposerWorksResponse>('/wikidata/works', {
    params: {
      composerId,
      limit,
    },
  })
}
